import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useCart } from "@/context/CartContext";

const API = process.env.REACT_APP_BACKEND_URL;

export const CartDrawer = () => {
  const { items, removeItem, setQty, subtotal, open, setOpen, keyOf, clear } = useCart();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [state, setState] = useState("");
  const [region, setRegion] = useState("west");
  const [loading, setLoading] = useState(false);

  const postage = region === "east" ? 15 : 10;
  const grandTotal = subtotal + postage;

  const checkout = async () => {
    if (!items.length) return;
    if (!name.trim() || !email.trim() || !phone.trim() || !address.trim() || !postcode.trim() || !state.trim()) {
      toast.error("Please fill in all delivery details.");
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity, size: i.size || null })),
          customer_name: name,
          customer_email: email,
          phone,
          address,
          postcode,
          state,
          region,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Unable to start checkout");
      localStorage.setItem("pahlawan_last_order", data.order_id);
      window.location.assign(data.checkout_url);
    } catch (e) {
      toast.error(e.message || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] bg-void/70 backdrop-blur-sm"
          onClick={() => setOpen(false)}
          data-testid="cart-overlay"
        >
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            onClick={(e) => e.stopPropagation()}
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col border-l border-neon-violet/40 bg-surface"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-white/10 p-5">
              <div className="flex items-center gap-2">
                <ShoppingBag className="h-5 w-5 text-neon-pink" />
                <h3 className="font-display text-xl">YOUR CART</h3>
              </div>
              <button onClick={() => setOpen(false)} className="rounded-full bg-black/40 p-2 text-white hover:bg-neon-pink" data-testid="cart-close">
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <ShoppingBag className="h-10 w-10 text-white/20" />
                  <p className="font-sub text-sm text-slate-400">Your cart is empty</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map((i) => (
                    <div key={keyOf(i)} className="flex gap-3 rounded-2xl border border-white/10 bg-void/40 p-3" data-testid={`cart-item-${i.product_id}`}>
                      <img src={i.image} alt={i.name} className="h-20 w-20 shrink-0 rounded-lg object-cover" />
                      <div className="flex-1">
                        <p className="font-sub text-sm font-bold leading-tight text-white">{i.name}</p>
                        {i.size && <p className="mt-0.5 font-mono-cap text-[9px] text-neon-cyan">Size {i.size}</p>}
                        <p className="mt-1 font-display text-base gradient-text">{i.price}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <button onClick={() => setQty(keyOf(i), i.quantity - 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-white/20 hover:border-neon-pink" data-testid={`cart-minus-${i.product_id}`}>
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="w-6 text-center font-sub text-sm">{i.quantity}</span>
                          <button onClick={() => setQty(keyOf(i), i.quantity + 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-white/20 hover:border-neon-pink" data-testid={`cart-plus-${i.product_id}`}>
                            <Plus className="h-3 w-3" />
                          </button>
                          <button onClick={() => removeItem(keyOf(i))} className="ml-auto text-slate-500 hover:text-neon-pink" data-testid={`cart-remove-${i.product_id}`}>
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                  <button onClick={clear} className="font-mono-cap text-[10px] text-slate-500 hover:text-neon-pink">Clear cart</button>
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 p-5">
                <div className="mb-3 space-y-1 text-sm">
                  <div className="flex items-center justify-between font-body text-slate-300">
                    <span>Subtotal</span><span>MYR {subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between font-body text-slate-300">
                    <span>Postage ({region === "east" ? "Sabah/Sarawak" : "Semenanjung"})</span>
                    <span>MYR {postage.toFixed(2)}</span>
                  </div>
                </div>
                <div className="mb-4 flex items-center justify-between border-t border-white/10 pt-3">
                  <span className="font-mono-cap text-[11px] text-slate-400">Total</span>
                  <span className="font-display text-2xl gradient-text" data-testid="cart-total">MYR {grandTotal.toFixed(2)}</span>
                </div>

                <div className="mb-3 grid grid-cols-2 gap-2">
                  <button onClick={() => setRegion("west")} className={`rounded-xl border py-2.5 font-mono-cap text-[10px] ${region === "west" ? "border-neon-pink bg-neon-pink/10 text-white" : "border-white/15 text-slate-300"}`} data-testid="region-west">West · RM10</button>
                  <button onClick={() => setRegion("east")} className={`rounded-xl border py-2.5 font-mono-cap text-[10px] ${region === "east" ? "border-neon-pink bg-neon-pink/10 text-white" : "border-white/15 text-slate-300"}`} data-testid="region-east">East · RM15</button>
                </div>

                <div className="space-y-2">
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="w-full rounded-xl border border-white/15 bg-void/60 px-4 py-2.5 font-body text-sm text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan" data-testid="cart-name" />
                  <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" placeholder="Email (for receipt)" className="w-full rounded-xl border border-white/15 bg-void/60 px-4 py-2.5 font-body text-sm text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan" data-testid="cart-email" />
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" className="w-full rounded-xl border border-white/15 bg-void/60 px-4 py-2.5 font-body text-sm text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan" data-testid="cart-phone" />
                  <input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Full delivery address" className="w-full rounded-xl border border-white/15 bg-void/60 px-4 py-2.5 font-body text-sm text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan" data-testid="cart-address" />
                  <div className="grid grid-cols-2 gap-2">
                    <input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Postcode" className="w-full rounded-xl border border-white/15 bg-void/60 px-4 py-2.5 font-body text-sm text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan" data-testid="cart-postcode" />
                    <input value={state} onChange={(e) => setState(e.target.value)} placeholder="State" className="w-full rounded-xl border border-white/15 bg-void/60 px-4 py-2.5 font-body text-sm text-white outline-none placeholder:text-slate-500 focus:border-neon-cyan" data-testid="cart-state" />
                  </div>
                </div>

                <button onClick={checkout} disabled={loading} className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-neon-pink to-neon-violet py-4 font-mono-cap text-[12px] text-white disabled:opacity-60" data-testid="cart-checkout-btn">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Opening HitPay…</> : `Checkout · MYR ${grandTotal.toFixed(2)}`}
                </button>
                <p className="mt-3 text-center font-mono-cap text-[9px] text-slate-500">Secure payment powered by HitPay</p>
              </div>
            )}
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
